import React, { useState } from 'react';
import { Modal, Chip, checkboxClasses, primaryButton, secondaryButton, smallNeutral } from '../components/ui';
import { canGoTo, ownerLabel, missingPrerequisites, publishTargets } from '../lib/files';
import { missingInVersions } from '../lib/publishReport';
import { plural } from '../lib/text';
import { VersionBreakdown, usePublishPreview } from './PublishReport';

// "Estás por liberar X updates, para X clientes, en X versiones": nada se publica sin pasar por acá.
export default function ConfirmPublishDialog({ files, filenames, clients, currentVersion, versions, onConfirm, onCancel }) {
    // `versions` son las elegidas en el selector de arriba; acá se puede dejar alguna afuera de esta liberación.
    const chosen = versions.map(v => v.id);
    const [extraVersions, setExtraVersions] = useState(() => chosen.filter(id => id !== currentVersion));
    const selected = filenames.map(name => files.all_files_map[name]).filter(Boolean);
    // De la versión actual ya se sabe qué falta; de las demás lo dice la vista previa del servidor.
    const localMissing = [...new Set(selected.flatMap(file => missingPrerequisites(files, file, publishTargets(files, file, clients))))]
        .filter(name => !filenames.includes(name));
    const [withDependencies, setWithDependencies] = useState(localMissing.length > 0);
    // Cada versión tiene sus prerrequisitos: si en alguna de las elegidas faltan, se propone liberarlos también.
    const [preview, retryPreview] = usePublishPreview(filenames, clients, chosen, loaded => { if (missingInVersions(loaded, chosen).length > 0) setWithDependencies(true); });

    // Sólo se libera en las versiones donde el update existe; de las demás se muestra por qué no.
    const info = Object.fromEntries((preview?.versions || []).map(v => [v.version, v]));
    const offered = versions.filter(v => v.id === currentVersion || !preview || info[v.id]?.available);
    const unavailable = versions.filter(v => !offered.includes(v));
    const allVersions = [currentVersion, ...extraVersions.filter(id => offered.some(v => v.id === id))];
    const toggleVersion = (id) => setExtraVersions(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
    const missing = preview ? missingInVersions(preview, allVersions) : localMissing;
    const report = preview && (withDependencies ? preview.with_dependencies : preview.plain);
    // En la versión actual se sabe quién ya lo tiene; en las demás se cuenta a todos los que les corresponde.
    const total = selected.reduce((sum, file) =>
        sum + publishTargets(files, file, clients).length + clients.filter(c => canGoTo(file, c)).length * extraVersions.length, 0);
    const blocked = missing.length > 0 && !withDependencies;
    // Sin el detalle a la vista no se libera: ni mientras se calcula ni si no se pudo calcular.
    const waiting = preview === undefined ? 'Esperá el detalle por versión para liberar.'
        : preview === null ? 'Sin el detalle por versión no se puede liberar.' : '';

    return (
        <Modal title="Confirmar liberación" onClose={onCancel} footer={<>
            {waiting && <span id="publish-waiting" className="mr-auto self-center text-xs font-medium text-slate-600">{waiting}</span>}
            <button type="button" onClick={onCancel} className={`${secondaryButton} whitespace-nowrap`}>Cancelar</button>
            <button type="button" disabled={!preview} title={waiting || undefined} aria-describedby={waiting ? 'publish-waiting' : undefined}
                onClick={() => onConfirm({ versions: allVersions, withDependencies })} className={`${primaryButton} whitespace-nowrap`}>Sí, liberar</button>
        </>}>
            <section>
                <p className="mb-2">Estás por liberar <strong>{plural(selected.length, 'update', 'updates')}</strong>:</p>
                <ul className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-44 overflow-y-auto">
                    {selected.map(file => (
                        <li key={file.name} className="flex items-center justify-between gap-2 px-3 py-1.5">
                            <span className="font-mono text-[13px] truncate">{file.name}</span>
                            <Chip tone={file.universal ? 'blue' : 'neutral'}>{ownerLabel(file)}</Chip>
                        </li>
                    ))}
                </ul>
            </section>

            <section>
                <p className="mb-2">Para <strong>{plural(clients.length, 'cliente', 'clientes')}</strong>:</p>
                <div className="flex flex-wrap gap-1.5">{clients.map(c => <span key={c} className="px-2 py-1 rounded-md bg-slate-100 font-mono text-[13px] font-medium">{c}</span>)}</div>
            </section>

            <section>
                <p className="mb-2">En <strong>{plural(allVersions.length, 'versión', 'versiones')}</strong>:</p>
                <div className="flex flex-wrap gap-1.5">
                    {offered.map(v => {
                        const isCurrent = v.id === currentVersion;
                        const on = isCurrent || extraVersions.includes(v.id);
                        return <button key={v.id} type="button" disabled={isCurrent} aria-pressed={on} onClick={() => toggleVersion(v.id)}
                            title={isCurrent ? undefined : on ? 'Tocá para dejarla afuera de esta liberación' : 'Tocá para sumarla a esta liberación'}
                            className={`px-3 py-1.5 rounded-md border font-mono text-[13px] font-medium ${on ? 'bg-zafiro-600 border-zafiro-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>{v.name}</button>;
                    })}
                </div>
                {unavailable.length > 0 && (
                    <ul aria-label="Versiones no disponibles" className="mt-2 space-y-0.5 text-xs text-slate-500">
                        {unavailable.map(v => <li key={v.id}><span className="font-mono font-medium text-slate-600">{v.name}</span> — no disponible: {info[v.id]?.reason || 'no se pudo consultar esta versión.'}</li>)}
                    </ul>
                )}
            </section>

            {preview === undefined && (
                <div role="status" className="flex items-center gap-3 p-4 rounded-lg border border-zafiro-600/30 bg-zafiro-50 text-zafiro-700">
                    <span className="zaf-spinner flex-shrink-0 text-2xl" />
                    <strong className="text-base">Calculando el detalle por versión…</strong>
                </div>
            )}
            {preview === null && (
                <div role="alert" className="flex items-center justify-between gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-700">
                    <span>
                        <strong className="block">No se pudo calcular el detalle por versión.</strong>
                        <span className="text-xs">Sin el detalle no se puede liberar. Reintentá, o cancelá y probá más tarde.</span>
                    </span>
                    <button type="button" onClick={retryPreview} className={`${smallNeutral} bg-white`}>Reintentar</button>
                </div>
            )}

            {report && (
                <section>
                    <p className="mb-2">Detalle por versión:</p>
                    <VersionBreakdown report={report} versions={offered.map(v => v.id).filter(id => allVersions.includes(id))} filenames={filenames}
                        hashes={Object.fromEntries(preview.versions.map(v => [v.version, v.files]))} />
                </section>
            )}

            {missing.length > 0 && (
                <label className="flex items-start gap-2 p-3 rounded-md bg-amber-50 text-amber-900 cursor-pointer">
                    <input type="checkbox" className={`mt-0.5 ${checkboxClasses}`} checked={withDependencies} onChange={() => setWithDependencies(w => !w)} />
                    <span><strong>Liberar también {plural(missing.length, 'prerrequisito que falta', 'prerrequisitos que faltan')}</strong>, primero y en orden:
                        <span className="block mt-1 font-mono text-xs">{missing.join(', ')}</span>
                        {blocked && <span className="block mt-1 font-semibold">Sin esto, los updates con prerrequisitos pendientes van a ser rechazados.</span>}
                    </span>
                </label>
            )}

            <p className="p-3 rounded-md bg-slate-50">
                <strong>Total: {report ? plural(report.successes.filter(entry => allVersions.includes(entry.version)).length, 'publicación', 'publicaciones') : `hasta ${plural(total, 'publicación', 'publicaciones')}`}.</strong>
            </p>
        </Modal>
    );
}
